import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Home, Languages, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/button'
import { iconComponent } from '@/components/ui/icon-picker'
import { useAuth } from '@/lib/AuthContext'
import { useLocale } from '@/lib/LocaleContext'
import { useRoute } from '@/lib/RouteContext'
import { sdk } from '@/lib/sdk'
import { contentLanguageFlag, contentLanguageLabel, localeForContentLanguage, sortContentLanguages } from '@/lib/contentLanguages'

interface ContentLanguageMismatchProps {
  /** The languages the content is for (the 403's `languages`). */
  languages: string[]
  /** What the link opens, when known: its name and icon on top. */
  scope?: { title?: string | null; icon?: string | null; description?: string | null } | null
}

/**
 * F-252: a direct link to a section, space or item of another language. The
 * content is not opened (David, 2026-11-05): one button per language of the
 * content switches the app (and the profile, signed in), and the menu that
 * comes back in that language opens it.
 */
export function ContentLanguageMismatch({ languages, scope }: ContentLanguageMismatchProps) {
  const { t } = useTranslation('common')
  const { changeLanguage } = useLocale()
  const { isAuthenticated } = useAuth()
  const { navigate } = useRoute()
  const [switching, setSwitching] = useState<string | null>(null)

  const options = sortContentLanguages(languages)
    .map((code) => ({ code, locale: localeForContentLanguage(code) }))
    .filter((option): option is { code: string; locale: NonNullable<typeof option.locale> } => Boolean(option.locale))

  const switchTo = async (code: string, locale: NonNullable<ReturnType<typeof localeForContentLanguage>>) => {
    setSwitching(code)
    try {
      await changeLanguage(locale)
      /* Same as the header selector: the choice follows the account. */
      if (isAuthenticated) sdk.members.updateMe({ language: locale }).catch(() => undefined)
    } finally {
      setSwitching(null)
    }
  }

  const Icon = (scope?.icon && iconComponent(scope.icon)) || Languages

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="space-access-denied" data-denial-reason="language">
      {scope?.title && <PageHeader icon={Icon} label={scope.title} description={scope.description || undefined} />}

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
        <section
          className="relative isolate mx-auto max-w-3xl overflow-hidden rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50 via-white to-primary-50 px-4 py-8 sm:px-10 sm:py-12"
          aria-labelledby="language-mismatch-title"
        >
          <span aria-hidden="true" className="pointer-events-none absolute -right-24 -top-28 -z-10 h-80 w-80 rounded-full bg-primary-200/40 blur-3xl" />
          <span aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-20 -z-10 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl" />

          <div className="mx-auto max-w-2xl text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-surface text-primary-600 shadow-sm">
              <Languages className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 id="language-mismatch-title" className="mt-4 text-balance text-2xl font-bold text-foreground sm:text-3xl">
              {t('contentLanguages.mismatch.title')}
            </h2>
            <p className="mt-3 text-foreground-secondary">{t('contentLanguages.mismatch.body')}</p>
          </div>

          <div role="alert" className="mx-auto mt-6 flex max-w-2xl flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
            {options.map(({ code, locale }, index) => {
              const flag = contentLanguageFlag(code)
              return (
                <Button
                  key={code}
                  variant={index === 0 ? 'primary' : 'outline'}
                  size="lg"
                  className={index === 0 ? 'w-full sm:w-auto' : 'w-full bg-surface sm:w-auto'}
                  disabled={switching !== null}
                  onClick={() => { void switchTo(code, locale) }}
                  data-switch-language={locale}
                >
                  {switching === code
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    : flag ? <span className="mr-2" aria-hidden="true">{flag}</span> : <Languages className="mr-2 h-4 w-4" aria-hidden="true" />}
                  {t('contentLanguages.mismatch.switchTo', { language: contentLanguageLabel(code) })}
                </Button>
              )
            })}
            <Button variant="outline" size="lg" className="w-full bg-surface sm:w-auto" onClick={() => navigate('/')}>
              <Home className="mr-2 h-4 w-4" />
              {t('access.denied.backHome')}
            </Button>
          </div>
        </section>
      </div>
    </div>
  )
}
